import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PortalAuth from './PortalAuth';
import PortalViewer from './PortalViewer';
import clientsConfig from './clients.json';
import './portal.css';

const PortalLayout = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [clientConfig, setClientConfig] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if client exists
    const config = clientsConfig[clientId];
    if (!config) {
      setError('Client portal not found');
      return;
    }
    setClientConfig(config);

    // Check for existing session
    const sessionKey = `portal_auth_${clientId}`;
    const session = localStorage.getItem(sessionKey);
    if (session) {
      try {
        const sessionData = JSON.parse(session);
        const sessionAge = Date.now() - sessionData.timestamp;
        // Session expires after 24 hours
        if (sessionAge < 24 * 60 * 60 * 1000) {
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem(sessionKey);
        }
      } catch (err) {
        localStorage.removeItem(sessionKey);
      }
    }
  }, [clientId]);

  const handleAuthenticated = async (passwordHash) => {
    if (!clientConfig) return false;

    // Verify password hash
    if (passwordHash === clientConfig.passwordHash) {
      const sessionKey = `portal_auth_${clientId}`;
      const sessionData = {
        timestamp: Date.now(),
        clientId
      };
      localStorage.setItem(sessionKey, JSON.stringify(sessionData));
      setIsAuthenticated(true);
      return true;
    }
    return false;
  };

  const handleLogout = () => {
    const sessionKey = `portal_auth_${clientId}`;
    localStorage.removeItem(sessionKey);
    setIsAuthenticated(false);
  };

  if (error) {
    return (
      <div className="portal-error-page">
        <h1>Portal Not Found</h1>
        <p>{error}</p>
        <button onClick={() => navigate('/gov')}>Return to Government Page</button>
      </div>
    );
  }

  if (!clientConfig) {
    return (
      <div className="portal-loading-page">
        <p>Loading portal...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <Helmet>
          <title>{clientConfig.name} - Specification Portal | Omnigeist</title>
          <meta name="description" content={`Secure specification and requirements portal for ${clientConfig.name}. Prepared by Omnigeist LLC for government procurement review.`} />
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <PortalAuth 
          onAuthenticated={handleAuthenticated} 
          clientName={clientConfig.name}
        />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{clientConfig.name} - Specification Portal | Omnigeist</title>
        <meta name="description" content={`Secure specification and requirements portal for ${clientConfig.name}. Prepared by Omnigeist LLC for government procurement review.`} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <PortalViewer 
        documents={clientConfig.documents}
        clientName={clientConfig.name}
        onLogout={handleLogout}
      />
    </>
  );
};

export default PortalLayout;

